import { Box, Alert, Tooltip } from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridSortModel,
  GridColumnHeaderParams,
} from '@mui/x-data-grid'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { enqueueSnackbar } from 'notistack'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router'
import { useFetchDevicePage } from '@/api/devicePage'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import { useFilters } from '@/hooks/useFilters'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import { FilterDrawer } from '@/components/FilterDrawer'
import DeviceInfoButton from '@/components/DeviceInfoButton'
import type { Device } from '@/pages/Device/Device'
import types from '@/pages/Device/DeviceType'
import { IsOnline, Uid } from '@/pages/utils/formatter'
import { DefaultFetchDeviceFilters, DurationTestStatus } from '@/api/definition'
import { CustomToolbar } from '@/components/DataGridToolBar'
import type { CustomToolbarProps } from '@/components/DataGridToolBar'
import type { FilterConfig, SelectedFilters } from '@/models/filters'
import type { CalibrationLoaderData } from './calibrationLoader'
import SerialEntryDialog from '@/pages/calibration/SerialEntryDialogBox'

type DeviceTypeMap = Record<string, string>
const deviceTypes = types as DeviceTypeMap

interface Props {
  setActiveStep: (step: number) => void
  onDeviceSelected: (device: Device) => void
}

const renderWrappedHeader = (params: GridColumnHeaderParams) => (
  <div
    style={{
      whiteSpace: 'normal',
      lineHeight: '1.5',
      fontWeight: 600,
      wordBreak: 'break-word',
      padding: '8px 0',
    }}
  >
    {params.colDef.headerName}
  </div>
)

const DeviceCalibrationSearchStep = ({
  setActiveStep,
  onDeviceSelected,
}: Props) => {
  const { t } = useTranslation(['di', 'app', 'common'])

  const { fetchDevicePage } = useFetchDevicePage()
  const { urlState, updateUrlState } = useTableUrlState()
  const filterConfig: FilterConfig = useDeviceTypeFilters()
  const filterHooks = useFilters()

  const loaderData = useLoaderData<CalibrationLoaderData>()
  const { error: loaderError } = loaderData

  const [deviceData, setDeviceData] = useState<Device[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(loaderError ?? null)
  const [dialogDevice, setDialogDevice] = useState<Device | null>(null)
  const [localSearchValue, setLocalSearchValue] = useState(urlState.searchTerm)

  const abortController = useRef(new AbortController())
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const calibrationFilters = useMemo(
    () => ({
      ...DefaultFetchDeviceFilters,
      inService: true,
      durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
    }),
    [],
  )

  const fetchData = useCallback(
    async (
      page: number,
      size: number,
      searchTerm: string,
      sortModel: GridSortModel,
      filters: SelectedFilters,
    ) => {
      abortController.current.abort()
      abortController.current = new AbortController()
      setIsLoading(true)
      setError(null)
      try {
        const sortBy =
          sortModel.length > 0
            ? [{ id: sortModel[0].field, desc: sortModel[0].sort === 'desc' }]
            : []
        const result = await fetchDevicePage(calibrationFilters, {
          pageIndex: page,
          pageSize: size,
          sortBy,
          globalFilter: searchTerm,
          selectedFilters: filters,
        })
        setDeviceData((result.data ?? []).map((d) => ({ ...d, id: d.uid })))
        setRowCount(result.rowCount ?? 0)
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message ?? 'Failed to load devices')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [fetchDevicePage, calibrationFilters],
  )

  useEffect(() => {
    fetchData(
      urlState.pageIndex,
      urlState.pageSize,
      urlState.searchTerm,
      urlState.sortModel,
      urlState.filters,
    )
    return () => abortController.current.abort()
  }, [
    urlState.pageIndex,
    urlState.pageSize,
    urlState.searchTerm,
    urlState.sortModel,
    urlState.filters,
    fetchData,
  ])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setLocalSearchValue(value)
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = setTimeout(() => {
        updateUrlState({ pageIndex: 0, searchTerm: value })
      }, 300)
    },
    [updateUrlState],
  )

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), [])
  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...urlState.filters }
      if (!newFilters[groupId]) newFilters[groupId] = {}
      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }
      updateUrlState({ pageIndex: 0, filters: newFilters })
    },
    [urlState.filters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({ pageIndex: 0, filters: {} })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = urlState.filters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [urlState.filters, handleFilterChange],
  )
  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const device = params.row as Device
      if (!device.editable) {
        enqueueSnackbar(t('di:device.rights.producerOnlyError'), {
          variant: 'error',
        })
        return
      }
      if (device.serial) {
        onDeviceSelected(device)
        setActiveStep(1)
      } else {
        setDialogDevice(device)
      }
    },
    [onDeviceSelected, setActiveStep, t],
  )

  const handleDialogClose = useCallback(() => setDialogDevice(null), [])

  const handleSerialConfirmed = useCallback(
    (updatedDevice: Device) => {
      setDialogDevice(null)
      onDeviceSelected(updatedDevice)
      setActiveStep(1)
    },
    [onDeviceSelected, setActiveStep],
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        minWidth: 400,
        maxWidth: 400,
        flex: 0,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'isOnline',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 1,
        minWidth: 80,
        sortable: true,
      },
      {
        field: 'type',
        headerName: t('di:device.type'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          const deviceType = deviceTypes[params.value] ?? params.value
          const deviceRoleName = params.row.deviceRoleName

          if (deviceRoleName) {
            return (
              <Tooltip title={deviceRoleName} placement="top" arrow>
                <Box
                  component="span"
                  sx={{ display: 'inline-block', width: '100%' }}
                >
                  {deviceType}
                </Box>
              </Tooltip>
            )
          }

          return deviceType
        },
        flex: 1,
        minWidth: 80,
        sortable: true,
      },
      {
        field: 'action',
        headerName: '',
        flex: 0,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        resizable: false,
        sortable: false,
        renderCell: (params) => <DeviceInfoButton params={params} />,
      },
    ],
    [t],
  )
  const activeFilterCount = Object.values(urlState.filters).reduce(
    (count, group) => count + Object.keys(group).length,
    0,
  )
  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': { whiteSpace: 'normal', lineHeight: '1.5' },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      >
        <DataGrid
          data-testid="calibration-device-search-table"
          rows={deviceData}
          getRowId={(row) => row.uid}
          columns={columns}
          autoHeight
          disableVirtualization
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          loading={isLoading}
          rowCount={rowCount}
          onRowClick={handleRowClick}
          paginationModel={{
            page: urlState.pageIndex,
            pageSize: urlState.pageSize,
          }}
          onPaginationModelChange={(params) =>
            updateUrlState({
              pageIndex: params.page,
              pageSize: params.pageSize,
            })
          }
          sortModel={urlState.sortModel}
          onSortModelChange={(model) =>
            updateUrlState({ pageIndex: 0, sortModel: model })
          }
          checkboxSelection={false}
          disableRowSelectionOnClick
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              enableSearch: true,
              enableFilters: true,
              onFilterClick: filterHooks.openDrawer,
              activeFilterCount,
              selectedFilters: urlState.filters,
              filterConfig,
              onRemoveFilter: handleRemoveFilter,
              searchValue: localSearchValue,
              onSearchChange: handleSearchChange,
            } as CustomToolbarProps,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          disableColumnFilter
          disableColumnMenu
          disableDensitySelector
          disableColumnSelector
          localeText={{ noRowsLabel: 'No records found' }}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: '#eee',
          paddingY: 3,
          display: 'flex',
          justifyContent: 'flex-start',
          borderTop: '1px solid #ddd',
        }}
      />

      <SerialEntryDialog
        open={Boolean(dialogDevice)}
        setOpen={(open) => {
          if (!open) setDialogDevice(null)
        }}
        device={dialogDevice}
        onConfirm={handleSerialConfirmed}
        onCancel={handleDialogClose}
        onClose={handleDialogClose}
      />

      <FilterDrawer
        open={filterHooks.isDrawerOpen}
        onClose={filterHooks.closeDrawer}
        filterConfig={filterConfig}
        selectedFilters={urlState.filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAllFilters}
      />
    </>
  )
}

export default DeviceCalibrationSearchStep
